import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const Business = () => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [newBusiness, setNewBusiness] = useState({
    businessName: '',
    businessType: '',
    industry: '',
    registrationNumber: '',
    taxId: '',
    email: '',
    phone: '',
    website: '',
    employeeCount: '',
    annualRevenue: '',
    status: 'active',
  });

  // Mock business data
  const [businesses, setBusinesses] = useState([
    {
      id: 1,
      businessName: 'a1 Technologies',
      businessType: 'Private Limited',
      industry: 'Information Technology',
      registrationNumber: 'CIN123456789',
      taxId: 'TAX987654321',
      email: 'info@a1.com',
      phone: '+91-9876543210',
      website: 'https://www.a1.com',
      employeeCount: '50-100',
      annualRevenue: '10-50 Crores',
      status: 'active',
    },
    {
      id: 2,
      businessName: 'a1 Retail Solutions',
      businessType: 'Partnership',
      industry: 'Retail',
      registrationNumber: 'REG456789123',
      taxId: 'TAX123456789',
      email: 'retail@a1.com',
      phone: '+91-9876543211',
      website: 'https://retail.a1.com',
      employeeCount: '10-50',
      annualRevenue: '5-10 Crores',
      status: 'active',
    },
    {
      id: 3,
      businessName: 'a1 Consulting',
      businessType: 'LLP',
      industry: 'Consulting',
      registrationNumber: 'LLP789123456',
      taxId: 'TAX456789123',
      email: 'consulting@a1.com',
      phone: '+91-9876543212',
      website: 'https://consulting.a1.com',
      employeeCount: '1-10',
      annualRevenue: '1-5 Crores',
      status: 'inactive',
    },
  ]);

  // Filter businesses
  const getFilteredBusinesses = () => {
    return businesses.filter((business) => {
      const matchesSearch =
        business.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.industry.toLowerCase().includes(searchQuery.toLowerCase()) ||
        business.registrationNumber.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || business.status === statusFilter;
      const matchesType = typeFilter === 'all' || business.businessType === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  };

  const filteredBusinesses = getFilteredBusinesses();

  // Pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!newBusiness.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!newBusiness.businessType) newErrors.businessType = 'Business type is required';
    if (!newBusiness.industry) newErrors.industry = 'Industry is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input
  const handleInputChange = (field, value) => {
    setNewBusiness(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Open modal for add/edit
  const handleOpenModal = (business = null) => {
    if (business) {
      setNewBusiness(business);
      setSelectedBusiness(business);
    } else {
      setNewBusiness({
        businessName: '',
        businessType: '',
        industry: '',
        registrationNumber: '',
        taxId: '',
        email: '',
        phone: '',
        website: '',
        employeeCount: '',
        annualRevenue: '',
        status: 'active',
      });
      setSelectedBusiness(null);
    }
    setErrors({});
    setOpenModal(true);
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (selectedBusiness) {
        // Edit
        setBusinesses(prev =>
          prev.map(business =>
            business.id === selectedBusiness.id ? { ...newBusiness, id: business.id } : business
          )
        );
        setSuccessMessage('Business updated successfully!');
      } else {
        // Add
        const newId = Math.max(...businesses.map(b => b.id), 0) + 1;
        setBusinesses(prev => [...prev, { ...newBusiness, id: newId }]);
        setSuccessMessage('Business added successfully!');
      }

      setOpenModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving business:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this business?')) {
      setIsLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        setBusinesses(prev => prev.filter(business => business.id !== id));
        setSuccessMessage('Business deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting business:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle view
  const handleView = (business) => {
    setSelectedBusiness(business);
    setOpenViewModal(true);
  };

  // Handle reset
  const handleReset = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPage(0);
    setSuccessMessage('');
  };

  // Get status chip style
  const getStatusStyle = (status) => {
    return status === 'active'
      ? { backgroundColor: '#e8f5e9', color: '#2e7d32', borderColor: '#c8e6c9' }
      : { backgroundColor: '#ffebee', color: '#c62828', borderColor: '#ffcdd2' };
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
              Business Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view business entities
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            px: 3,
            py: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              backgroundColor: 'primary.dark',
              boxShadow: '0 4px 6px 2px rgba(25, 118, 210, .4)',
            },
          }}
        >
          Add Business
        </Button>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Search and Filter Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              placeholder="Search businesses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Business Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Business Type"
                sx={{ borderRadius: 2, backgroundColor: '#f5f5f5' }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="Private Limited">Private Limited</MenuItem>
                <MenuItem value="Public Limited">Public Limited</MenuItem>
                <MenuItem value="Partnership">Partnership</MenuItem>
                <MenuItem value="LLP">LLP</MenuItem>
                <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
                sx={{ borderRadius: 2, backgroundColor: '#f5f5f5' }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{
                height: 56,
                borderRadius: 2,
                borderColor: '#1976d2',
                color: '#1976d2',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: '#e3f2fd',
                },
              }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>Business Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>Industry</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBusinesses
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((business) => (
                  <TableRow
                    key={business.id}
                    hover
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ color: '#1976d2' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {business.businessName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {business.registrationNumber}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={business.businessType}
                        size="small"
                        sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>{business.industry}</TableCell>
                    <TableCell>
                      <Chip
                        label={business.status.charAt(0).toUpperCase() + business.status.slice(1)}
                        size="small"
                        sx={{
                          ...getStatusStyle(business.status),
                          fontWeight: 600,
                          border: '1px solid',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{business.phone}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">{business.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(business)}
                            sx={{ color: '#1976d2' }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(business)}
                            sx={{ color: '#ed6c02' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(business.id)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              {filteredBusinesses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <BusinessIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No businesses found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search or filter criteria
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredBusinesses.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          
        />
      </Paper>

      {/* Add/Edit Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 600,
          }}
        >
          {selectedBusiness ? 'Edit Business' : 'Add New Business'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Name *"
                value={newBusiness.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                error={!!errors.businessName}
                helperText={errors.businessName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.businessType}>
                <InputLabel>Business Type *</InputLabel>
                <Select
                  value={newBusiness.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  label="Business Type *"
                >
                  <MenuItem value="Private Limited">Private Limited</MenuItem>
                  <MenuItem value="Public Limited">Public Limited</MenuItem>
                  <MenuItem value="Partnership">Partnership</MenuItem>
                  <MenuItem value="LLP">Limited Liability Partnership</MenuItem>
                  <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
                </Select>
                {errors.businessType && (
                  <Typography variant="caption" color="error">
                    {errors.businessType}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.industry}>
                <InputLabel>Industry *</InputLabel>
                <Select
                  value={newBusiness.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  label="Industry *"
                >
                  <MenuItem value="Information Technology">Information Technology</MenuItem>
                  <MenuItem value="Healthcare">Healthcare</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Manufacturing">Manufacturing</MenuItem>
                  <MenuItem value="Retail">Retail</MenuItem>
                  <MenuItem value="Consulting">Consulting</MenuItem>
                  <MenuItem value="Education">Education</MenuItem>
                  <MenuItem value="Real Estate">Real Estate</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
                {errors.industry && (
                  <Typography variant="caption" color="error">
                    {errors.industry}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Registration Number"
                value={newBusiness.registrationNumber}
                onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax ID"
                value={newBusiness.taxId}
                onChange={(e) => handleInputChange('taxId', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newBusiness.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={newBusiness.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                value={newBusiness.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Employee Count</InputLabel>
                <Select
                  value={newBusiness.employeeCount}
                  onChange={(e) => handleInputChange('employeeCount', e.target.value)}
                  label="Employee Count"
                >
                  <MenuItem value="1-10">1-10</MenuItem>
                  <MenuItem value="11-50">11-50</MenuItem>
                  <MenuItem value="51-100">51-100</MenuItem>
                  <MenuItem value="101-500">101-500</MenuItem>
                  <MenuItem value="501-1000">501-1000</MenuItem>
                  <MenuItem value="1000+">1000+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Annual Revenue</InputLabel>
                <Select
                  value={newBusiness.annualRevenue}
                  onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
                  label="Annual Revenue"
                >
                  <MenuItem value="Under 1 Crore">Under 1 Crore</MenuItem>
                  <MenuItem value="1-5 Crores">1-5 Crores</MenuItem>
                  <MenuItem value="5-10 Crores">5-10 Crores</MenuItem>
                  <MenuItem value="10-50 Crores">10-50 Crores</MenuItem>
                  <MenuItem value="50-100 Crores">50-100 Crores</MenuItem>
                  <MenuItem value="100+ Crores">100+ Crores</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={newBusiness.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenModal(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Modal */}
      <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Business Details
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedBusiness && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Business Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedBusiness.businessName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Business Type
                </Typography>
                <Typography variant="body1">{selectedBusiness.businessType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry
                </Typography>
                <Typography variant="body1">{selectedBusiness.industry}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Registration Number
                </Typography>
                <Typography variant="body1">{selectedBusiness.registrationNumber}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Tax ID
                </Typography>
                <Typography variant="body1">{selectedBusiness.taxId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedBusiness.status.charAt(0).toUpperCase() + selectedBusiness.status.slice(1)}
                  size="small"
                  sx={{
                    ...getStatusStyle(selectedBusiness.status),
                    fontWeight: 600,
                    border: '1px solid',
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{selectedBusiness.email}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Phone
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{selectedBusiness.phone}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Website
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LanguageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body1">{selectedBusiness.website}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Employee Count
                </Typography>
                <Typography variant="body1">{selectedBusiness.employeeCount}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Annual Revenue
                </Typography>
                <Typography variant="body1">{selectedBusiness.annualRevenue}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenViewModal(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Business;

